import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ListTodo, LogOut, CalendarOff, WifiOff, RefreshCw, AlertTriangle, Trash2, Bell, ClipboardList, Trophy, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { useOnline, useOutbox, syncOutbox, removeItem, retryItem } from "@/lib/offline";
import { Logo } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

const LABEL = { arrive: "Arrivo sul posto", photo: "Foto", report: "Rapportino" };

const SyncBar = () => {
  const online = useOnline();
  const items = useOutbox();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const errors = items.filter((x) => x.status === "error");

  const sync = async () => {
    setBusy(true);
    const sent = await syncOutbox(api).catch(() => 0);
    setBusy(false);
    if (sent) toast.success(`${sent} operazioni sincronizzate`);
  };
  useEffect(() => {
    if (online && items.some((x) => x.status === "pending")) sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, items.length]);

  if (online && !items.length) return null;
  return (
    <div className={`border-b px-4 py-2 text-sm ${!online ? "bg-amber-100 text-amber-900" : errors.length ? "bg-red-50 text-red-800" : "bg-sky-50 text-sky-900"}`} data-testid="tech-sync-bar">
      <div className="flex items-center gap-2">
        {!online ? <WifiOff className="h-4 w-4 shrink-0" /> : errors.length ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <RefreshCw className={`h-4 w-4 shrink-0 ${busy ? "animate-spin" : ""}`} />}
        <span className="flex-1" data-testid="tech-sync-status">
          {!online ? "Sei offline: puoi continuare a lavorare." : errors.length ? `${errors.length} operazioni non accettate` : "Operazioni in attesa di invio"}
          {items.length > 0 && <> · <strong>{items.length}</strong> in coda</>}
        </span>
        {items.length > 0 && <button className="text-xs font-semibold underline" onClick={() => setOpen(!open)} data-testid="tech-sync-details">{open ? "Chiudi" : "Dettagli"}</button>}
        {online && items.length > 0 && <Button size="sm" variant="outline" className="h-7 bg-white" onClick={sync} disabled={busy} data-testid="tech-sync-now">Invia ora</Button>}
      </div>
      {open && (
        <ul className="mt-2 space-y-1" data-testid="tech-sync-list">
          {items.map((x) => (
            <li key={x.id} className="flex items-center gap-2 bg-white/70 px-2 py-1.5 text-xs">
              <span className="flex-1"><strong>{LABEL[x.type]}</strong> · {x.order_code}{x.error && <span className="block text-red-700">{String(x.error)}</span>}</span>
              {x.status === "error" && <button onClick={() => retryItem(x.id).then(sync)} className="font-semibold underline" data-testid={`tech-sync-retry-${x.id}`}>Riprova</button>}
              {x.status === "error" && <button onClick={() => removeItem(x.id)} aria-label="Scarta" data-testid={`tech-sync-discard-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TechNotices = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const load = () => api.get("/hr/notifications").then((r) => {
      setList(r.data);
      if (r.data.some((n) => !n.read)) setOpen(true);
    }).catch(() => {});
    load();
    window.addEventListener("tech:notices", load);
    return () => window.removeEventListener("tech:notices", load);
  }, []);
  const unread = list.filter((n) => !n.read);
  const close = () => {
    setOpen(false);
    if (unread.length) api.post("/hr/notifications/read").then(() => setList(list.map((n) => ({ ...n, read: true })))).catch(() => {});
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="relative p-2" aria-label="Avvisi" data-testid="tech-notifications-button">
        <Bell className="h-5 w-5" />
        {unread.length > 0 && <span className="absolute right-0.5 top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-testid="tech-notifications-badge">{unread.length}</span>}
      </button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-md" data-testid="tech-notifications-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Avvisi</DialogTitle></DialogHeader>
          {list.length === 0 && <p className="text-sm text-muted-foreground">Nessun avviso.</p>}
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {list.slice(0, 15).map((n) => (
              <div key={n.id} className={`border-l-4 p-3 text-sm ${n.tone === "success" ? "border-emerald-600" : n.tone === "info" ? "border-sky-600" : "border-red-600"} ${n.read ? "bg-muted/40" : "bg-white shadow-sm"}`} data-testid={`tech-notification-${n.id}`}>
                <div className="font-semibold">{n.title}</div>
                <div className="mt-0.5">{n.text}</div>
                <div className="mt-1 font-mono text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</div>
              </div>
            ))}
          </div>
          <Button onClick={close} data-testid="tech-notifications-close">Ho capito</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const NavItem = ({ to, icon: Icon, label, testId, end, badge }) => (
  <NavLink to={to} end={end} className={({ isActive }) => `relative flex flex-1 flex-col items-center gap-1 py-3 text-xs ${isActive ? "text-white" : "text-zinc-400"}`} data-testid={testId}>
    <Icon className="h-6 w-6" />{label}
    {badge > 0 && <span className="absolute right-[22%] top-1.5 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-testid={`${testId}-badge`}>{badge}</span>}
  </NavLink>
);

export default function TechLayout() {
  const { user, logout } = useAuth();
  const items = useOutbox();
  const location = useLocation();
  const [invCount, setInvCount] = useState(0);
  useEffect(() => { api.get("/tech/counts").then((r) => setInvCount(r.data.length)).catch(() => {}); }, [location.pathname]);
  const exit = () => {
    if (items.length && !window.confirm(`Hai ${items.length} operazioni non ancora inviate. Uscendo potrebbero andare perse. Vuoi uscire comunque?`)) return;
    logout();
  };
  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col bg-[#F4F5F7]" data-testid="tech-layout">
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="brand-stripe h-1" />
        <div className="flex items-center justify-between px-4 py-3">
          <Logo className="h-7" />
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-sm font-semibold leading-tight" data-testid="tech-user-name">{user.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tecnico</div>
            </div>
            <a href={`${API}/tutorials/app-tecnico.pdf`} className="flex h-9 w-9 items-center justify-center text-muted-foreground" aria-label="Tutorial App tecnico" data-testid="tech-tutorial-link"><BookOpen className="h-5 w-5" /></a>
            <TechNotices />
          </div>
        </div>
        <SyncBar />
      </header>
      <main className="flex-1 px-4 pb-28 pt-5"><Outlet /></main>
      <nav className="fixed inset-x-0 bottom-0 z-40 mx-auto flex max-w-xl border-t bg-[#0B0F14] text-white">
        <NavItem to="/tecnico" end icon={ListTodo} label="Interventi" testId="tech-nav-orders" />
        <NavItem to="/tecnico/produzione" icon={Trophy} label="Produzione" testId="tech-nav-production" />
        <NavItem to="/tecnico/assenze" icon={CalendarOff} label="Assenze" testId="tech-nav-absences" />
        <NavItem to="/tecnico/inventario" icon={ClipboardList} label="Inventario" testId="tech-nav-inventory" badge={invCount} />
        <button onClick={exit} className="flex flex-1 flex-col items-center gap-1 py-3 text-xs text-zinc-400" data-testid="tech-logout-button">
          <LogOut className="h-6 w-6" />Esci
        </button>
      </nav>
    </div>
  );
}
